import './styles/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/formatters/contractStatus';
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import ConfirmationModal from '../../templates/ConfirmationModal';
import AmendmentModal from './modals/AmendmentModal';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSearch } from "react-icons/fa";
import PageForm from "../../templates/PageForm";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import BadgeStatus from "../../atoms/BadgeStatus";
import ReceivableModalView from "./modals/ReceivableModalView";
import ContractFileUpload from './components/files/ContractFileUpload';
import ArtifactFilesUpload from './components/files/ArtifactFilesUpload';
import PersonSearchModal from "./modals/PersonSearchModal";
import ReceivablesManager from './components/receivables/ReceivablesManager';
import ReimportReceivablesButton from './components/receivables/ReimportReceivablesButton';
import InstallmentManager from './components/form/InstallmentManager';
import DisbursementManager from './components/form/DisbursementManager';
import BaseCalculationSection from './components/form/BaseCalculationSection';
import BillingConfigSection, { EMPTY_BILLING_CONFIG } from './components/form/BillingConfigSection';
import BillingStatusBadge from './components/status/BillingStatusBadge';
import { useBillingStatus } from './hooks/data/useBillingStatus';
import ContractActionDropdown from './components/status/ContractActionDropdown';
import ContractsAsReceivablesManager, { ContractsAsReceivablesData } from './components/receivables/ContractsAsReceivablesManager';
import BusinessPartnerManager, { BusinessPartnerData } from './components/form/BusinessPartnerManager';
import CreditorSelector from './components/form/CreditorSelector';
import { CreditorPayload } from '../../../services/contractsManagementApi';
import CollateralsTabManager from './components/collaterals/CollateralsTabManager';
import MainRiskSelector, { MainRisk } from './components/form/MainRiskSelector';
import AmendmentReasonSection from './components/form/AmendmentReasonSection';
import { ReceivableItem, InstallmentItem, BaseCalculation, BillingConfig, Collaterals, ContractAsReceivableItem, EMPTY_COLLATERALS, hasContractsAsReceivablesMissingData } from './types/operations';
import { PersonSearchResult, JsonUploadData } from './types/api-responses';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { useFileDownload } from './components/files/useFileDownload';
import appConfig from '../../../config/app-config';
import { CREDIT_OPERATION_TYPE_OPTIONS } from './constants/creditOperationTypes';
import { RECEIVING_ACCOUNT_OPTIONS } from './constants/receivingAccountOptions';
import { useContractActions } from './hooks/contract/useContractActions';
import { useReceivablesDownloader } from './hooks/data/useReceivablesDownloader';
import { mapReceivableFromApi } from './utils/mappers/dataMappers';
import { mapReceivablesAssignmentApiResponseToForm } from './utils/mappers/responseMappers';
import { buildReceivablesAssignmentPayload } from './utils/payload/receivablesAssignmentPayloadBuilder';
import { useContractSubmit } from './hooks/contract/useContractSubmit';
import { useContractFetch } from './hooks/contract/useContractFetch';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

type Disbursement = {
    amount: number;
    date: string;
};

type Form = {
    total_amount: number;
    loan_amount: number;
    expiration_date: string;
    emission_date: string;
    approved_on: string;
    created_at: string;
    guarantee_percentage: number;
    operation_number: string;
    status?: string;
    sequencial_number: number;
    external_link: string;
    type: string;
    assignor: string;
    recipient: string;
    originator: string;
    rebate_amount: number;
    assignment_type_name: string;
    with_recourse: boolean;
    credit_product_type_name: string;
    receiving_account_name?: string;
    receivables: { items: ReceivableItem[]; total: number; quantity: number; overcollateral?: boolean | null; spreadsheet_processed?: boolean; spreadsheet_items_count?: number; operation_id?: string };
    contracts_as_receivables: { items: ContractAsReceivableItem[]; total: number; quantity: number };
    collaterals: Collaterals;
    disbursement: {
        disbursements: Disbursement[];
        totalAmount: number;
    };
    base_calculation?: BaseCalculation;
    billing_config?: BillingConfig;
    installments: InstallmentItem[];
    main_risk: MainRisk;
};

const ReceivablesAssignmentPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.credit_operation_id || params.id;
    const { handleDownload } = useFileDownload();
    const isLoadingForm = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [isAmendmentModalOpen, setIsAmendmentModalOpen] = useState(false);
    const [amendmentReason, setAmendmentReason] = useState<string>('');
    const [amendmentNumber, setAmendmentNumber] = useState<number>(0);
    const [isContractLoaded, setIsContractLoaded] = useState(false);
    const [contractsAsReceivables, setContractsAsReceivables] = useState<ContractsAsReceivablesData>({
        items: [],
        files: new Map(),
        existingFiles: new Map()
    });
    const [businessPartner, setBusinessPartner] = useState<BusinessPartnerData | null>(null);
    const [creditor, setCreditor] = useState<CreditorPayload | null>(null);
    const [billingStatus, setBillingStatus] = useState<{ billed?: boolean; billing_info?: string }>({});
    const { billingData, isPolling, startPolling, fetchBillingStatus } = useBillingStatus(credit_operation_id);
    const apiVersion = useSignal('');
    const [hasInvalidDueDates, setHasInvalidDueDates] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedDestinatario, setSelectedDestinatario] = useState('');
    const [selectedCedente, setSelectedCedente] = useState('');
    const [assignorData, setAssignorData] = useState<any>(null);
    const [recipientData, setRecipientData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const contractFiles = useSignal<File[]>([]);
    const artifactFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [selectedReceivable, setSelectedReceivable] = useState<ReceivableItem | null>(null);
    const [isReceivableModalOpen, setIsReceivableModalOpen] = useState(false);

    const form = useSignal<Form>({
        total_amount: 0,
        loan_amount: 0,
        expiration_date: '',
        emission_date: '',
        approved_on: '',
        created_at: '',
        guarantee_percentage: 0,
        operation_number: "",
        sequencial_number: 0,
        external_link: "",
        type: "",
        assignor: "",
        recipient: "",
        originator: "",
        rebate_amount: 0,
        assignment_type_name: "",
        with_recourse: false,
        credit_product_type_name: "",
        receiving_account_name: '',
        receivables: { items: [], total: 0, quantity: 0, overcollateral: null, spreadsheet_processed: false },
        contracts_as_receivables: { items: [], total: 0, quantity: 0 },
        collaterals: EMPTY_COLLATERALS,
        disbursement: {
            disbursements: [],
            totalAmount: 0
        },
        base_calculation: {
            tir_annual: null,
            tir_monthly: null,
            spread_annual: null,
            vpl: null,
            vpl_margin: null,
            duration_days: null
        },
        billing_config: EMPTY_BILLING_CONFIG,
        installments: [],
        main_risk: { type: 'DRAWEE', uuid: '', name: '' }
    });

    const validateAllFields = useCallback((): boolean => {
        const f = form.value;
        const rules: Array<{ condition: boolean; label: string; fieldName: string }> = [
            { condition: !creditor, label: 'Credor', fieldName: 'creditor' },
            { condition: !f.external_link?.trim(), label: 'Card JIRA', fieldName: 'external_link' },
            { condition: !f.credit_product_type_name?.trim(), label: 'Tipo de Operação de Crédito', fieldName: 'credit_product_type_name' },
            { condition: !f.receiving_account_name?.trim(), label: 'Conta para Recebimento', fieldName: 'receiving_account_name' },
            { condition: !f.emission_date?.trim(), label: 'Data de Emissão', fieldName: 'emission_date' },
            { condition: !f.approved_on?.trim(), label: 'Data de Aprovação', fieldName: 'approved_on' },
            { condition: !selectedDestinatario || selectedDestinatario === '00000000-0000-0000-0000-000000000000', label: 'Beneficiário', fieldName: 'recipient' },
            { condition: !f.loan_amount, label: 'Valor Financiado', fieldName: 'loan_amount' },
            { condition: !f.total_amount, label: 'Valor Total', fieldName: 'total_amount' },
            { condition: !f.disbursement.totalAmount || f.disbursement.totalAmount <= 0, label: 'Desembolsos', fieldName: 'disbursement' },
            { condition: f.billing_config?.generate_billets === null, label: 'Gerar boletos para cobrança', fieldName: 'generate_billets' },
            { condition: f.billing_config?.automatic_preventive_collection === null, label: 'Cobrança preventiva automática', fieldName: 'automatic_preventive_collection' },
            { condition: f.billing_config?.automatic_overdue_collection === null, label: 'Cobrança de vencidos automática', fieldName: 'automatic_overdue_collection' },
            { condition: f.credit_product_type_name !== 'CONTRACT_ASSIGNMENT' && (f.receivables?.overcollateral === null || f.receivables?.overcollateral === undefined), label: 'Operação com overcollateral', fieldName: 'overcollateral' },
            { condition: f.credit_product_type_name === 'CONTRACT_ASSIGNMENT' && !contractsAsReceivables.items?.length, label: 'Contrato Cedido', fieldName: 'contracts_as_receivables' },
            { condition: f.credit_product_type_name === 'CONTRACT_ASSIGNMENT' && hasContractsAsReceivablesMissingData(contractsAsReceivables.items || []), label: 'Dados do Contratante (documento, nome, parcela, data operação, valor presente, vencimento)', fieldName: 'contracts_as_receivables_drawee' },
            { condition: !f.main_risk?.type, label: 'Risco Principal', fieldName: 'main_risk' },
            { condition: f.main_risk?.type === 'OTHER' && !f.main_risk?.uuid, label: 'Risco Principal (selecione a pessoa)', fieldName: 'main_risk' },
        ];

        const failed = rules.filter(r => r.condition);
        errorFields.value = failed.map(r => ({ fieldName: r.fieldName, message: 'Campo obrigatório' }));

        if (failed.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${failed.map(r => r.label).join(', ')}`);
            return false;
        }
        return true;
    }, [form, selectedDestinatario, contractsAsReceivables, errorFields, creditor]);

    const { handleCancelContract, handleFinishContract, handleBillingProcess } = useContractActions({
        apiUrl: API_URL || '',
        apiKey: API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoadingForm.value = v; },
        hasUnsavedChanges,
        hasInvalidDueDates,
        validateAllFields,
        setIsCancelModalOpen,
        setIsFinishModalOpen,
        onRefresh: () => { fetchContractData(); startPolling(); },
        contractStatus: form.value.status,
    });

    const { submitContract } = useContractSubmit({
        apiUrl: API_URL,
        apiKey: API_KEY,
        endpoint: '/contracts/receivables_assignment/update',
        setLoading: (v: boolean) => { isLoadingForm.value = v; }
    });

    const handleChange = useCallback((field: string, value: string | number | boolean) => {
        form.value = { ...form.value, [field]: value };
        setHasUnsavedChanges(true); // Marcar como alterado ao editar
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    const handleJsonDataLoaded = (data: JsonUploadData) => {
        const receivablesData = data.duplicates || data.receivables;
        if (receivablesData?.items) {
            // Substitui integralmente os recebíveis existentes (não concatena).
            const updatedItems: ReceivableItem[] = receivablesData.items.map(mapReceivableFromApi);
            const operationId = data.operation_id ?? (receivablesData as any)?.operation_id ?? form.value.receivables?.operation_id;

            form.value = {
                ...form.value,
                total_amount: data.total_amount || form.value.total_amount,
                operation_number: data.operation_code || form.value.operation_number,
                receivables: {
                    ...form.value.receivables,
                    quantity: updatedItems.length,
                    total: updatedItems.reduce((sum, item) => sum + item.receivable_amount, 0),
                    items: updatedItems,
                    operation_id: operationId,
                }
            };
            setHasUnsavedChanges(true);
        }
    };

    useReceivablesDownloader(
        form.value.sequencial_number || 0,
        form.value.receivables?.items.filter(item =>
            item.document_id && item.document_id.trim() !== '' && item.receivable_amount > 0
        ).length || 0,
        handleJsonDataLoaded,
        amendmentNumber,
        isContractLoaded,
        form.value.credit_product_type_name === 'CONTRACT_ASSIGNMENT'
    );

    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!selectedDestinatario) {
            toast.error('Beneficiário é obrigatório');
            return;
        }

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        if (!creditor) {
            toast.error('Selecione um credor');
            return;
        }

        if (!form.value.external_link || form.value.external_link.trim() === '') {
            toast.error('Card JIRA é obrigatório');
            return;
        }

        if (!form.value.receiving_account_name || form.value.receiving_account_name.trim() === '') {
            toast.error('Conta para Recebimento é obrigatória');
            return;
        }

        // Validar Valor Nominal obrigatório
        if (!form.value.disbursement.totalAmount || form.value.disbursement.totalAmount <= 0) {
            toast.error('Valor Nominal é obrigatório');
            return;
        }

        // Validar se soma dos desembolsos não excede o Valor Nominal
        const sumDisbursements = form.value.disbursement.disbursements.reduce((sum, d) => sum + d.amount, 0);
        if (sumDisbursements > form.value.disbursement.totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${sumDisbursements.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${form.value.disbursement.totalAmount.toFixed(2)})`);
            return;
        }

        // Validar planilha processada
        if (form.value.receivables?.items?.some(item => !item.receivable_due_date || item.receivable_due_date.trim() === '')) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de salvar.');
            return;
        }

        if (form.value.receivables?.spreadsheet_processed && form.value.receivables?.spreadsheet_items_count && form.value.receivables.spreadsheet_items_count > 0) {
            const receivablesCount = form.value.receivables.items?.length || 0;
            const spreadsheetCount = form.value.receivables.spreadsheet_items_count || 0;
            const hasDivergences = form.value.receivables.items?.some(item => item.has_value_divergence) || false;

            if (receivablesCount !== spreadsheetCount) {
                toast.error(`Planilha processada: ${spreadsheetCount} itens na planilha, mas ${receivablesCount} recebíveis na lista. Todos os itens devem estar presentes.`);
                return;
            }

            if (hasDivergences) {
                toast.error('Planilha processada com divergências de valores. Corrija as divergências antes de salvar.');
                return;
            }
        }

        isLoadingForm.value = true;
        try {
            const payload = buildReceivablesAssignmentPayload({
                form: form.value,
                creditOperationId: credit_operation_id,
                selectedCedente,
                selectedDestinatario,
                assignorData,
                recipientData,
                contractFiles: contractFiles.value,
                artifactFiles: artifactFiles.value,
                existingArtifactFiles,
                removedArtifactFiles,
                existingContractFile,
                removedContractFile,
                contractGuaranteeFile: Array.from(contractsAsReceivables.files.values())[0] || null,
                contractGuaranteeFiles: Array.from(contractsAsReceivables.files.values()),
                businessPartner,
                creditor,
            });

            await submitContract(
                payload,
                { contractFiles: contractFiles.value, artifactFiles: [...artifactFiles.value, ...Array.from(contractsAsReceivables.files.values())] },
                {
                    successMessage: "Cessão de recebíveis atualizada com sucesso!",
                    onSuccess: async () => {
                        setHasUnsavedChanges(false);
                        await fetchContractData();
                        contractFiles.value = [];
                        artifactFiles.value = [];
                        setRemovedContractFile(false);
                        setRemovedArtifactFiles([]);
                    }
                }
            );
        } finally {
            isLoadingForm.value = false;
        }
    };



    const handleSelectPerson = (selected: PersonSearchResult) => {
        const cnpjDoc = selected.documents?.find((doc) => doc.type === 'CNPJ');
        const cpfDoc = selected.documents?.find((doc) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';

        if (!document || document.trim() === '') {
            toast.error("Beneficiário sem documento cadastrado");
            return;
        }

        setSelectedDestinatario(selected.uuid);
        handleChange("recipient", selected.name || '');

        setRecipientData({
            ...recipientData,
            name: selected.name,
            document: document,
            person_id: selected.uuid,
            is_active: true,
            is_primary: false,
            effective_from: new Date().toISOString()
        });
    };

    const { fetchContract } = useContractFetch({
        apiUrl: API_URL || '',
        apiKey: API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoadingForm.value = v; },
        setHasUnsavedChanges,
        setIsContractLoaded,
    });

    const fetchContractData = async () => {
        await fetchContract({
            onMetadata: (metadata) => {
                if (metadata.apiVersion) apiVersion.value = metadata.apiVersion;
                if (metadata.amendmentReason) setAmendmentReason(metadata.amendmentReason);
                if (metadata.amendmentNumber) setAmendmentNumber(metadata.amendmentNumber);
                setBillingStatus(metadata.billingStatus);
            },
            onBusinessPartner: setBusinessPartner,
            onCreditor: setCreditor,
            onFiles: (files) => {
                setExistingContractFile(files.contractFile);
                setExistingArtifactFiles(files.artifactFiles);
            },
            onContractsAsReceivables: setContractsAsReceivables,
            onData: ({ data }) => {
                const mapped = mapReceivablesAssignmentApiResponseToForm(data, form.value);
                form.value = mapped.form as Form;

                if (mapped.assignorParticipant) {
                    setAssignorData(mapped.assignorParticipant);
                    setSelectedCedente(mapped.assignorParticipant.person_id);
                }
                if (mapped.recipientParticipant) {
                    setRecipientData(mapped.recipientParticipant);
                    setSelectedDestinatario(mapped.recipientParticipant.person_id);
                    form.value = { ...form.value, recipient: mapped.recipientParticipant.name || '' };
                }
            },
        });
    };

    useEffect(() => {
        if (credit_operation_id) {
            fetchContractData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credit_operation_id]);

    const handleCancel = () => {
        navigate(-1);
    };

    const isFormDisabled = isContractDisabled(form.value.status);

    return (
        <Fragment>
            <PageForm
                pageTitle="Cessão de Recebíveis"
                apiVersion={apiVersion.value}
                isLoading={isLoadingForm.value}
                breadcrumb={[
                    { label: "Operações", url: "/operations" },
                    { label: "Cessão de Recebíveis", url: "#" }
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button type="button"
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={isLoadingForm.value}
                        >
                            Voltar
                        </button>

                        <ContractActionDropdown
                            onSave={handleSubmit}
                            onCancel={() => setIsCancelModalOpen(true)}
                            onFinish={() => setIsFinishModalOpen(true)}
                            onBilling={handleBillingProcess}
                            onAmendment={() => setIsAmendmentModalOpen(true)}
                            creditOperationId={credit_operation_id}
                            apiUrl={API_URL || ''}
                            apiKey={API_KEY || ''}
                            setLoading={(v: boolean) => { isLoadingForm.value = v; }}
                            onRegistered={() => { fetchContractData(); }}
                            isLoading={isLoadingForm.value}
                            isFormDisabled={isFormDisabled}
                            hasContractId={!!credit_operation_id}
                            contractStatus={form.value.status || ''}
                            canAmend={form.value.with_recourse !== false}
                            isBilled={!!billingStatus.billed}
                        />
                    </div>
                }
            >
                <div className="container mx-auto px-2 py-4">
                    <CreditorSelector
                        value={creditor}
                        onChange={(c) => {
                            setCreditor(c);
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoadingForm.value}
                        readOnly={isFormDisabled}
                    />

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Informações Gerais</h3>
                    <div className="mb-4 mt-2">

                        <BadgeStatus
                            color={getStatusColor(form.value.status)}
                            text={form.value.status || ''}
                        />
                    </div>
                    {amendmentNumber > 0 && <AmendmentReasonSection reason={amendmentReason} />}
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <TextEdit
                                label="Número do Contrato"
                                id="sequencial_number"
                                value={amendmentNumber > 0 ? `${form.value.sequencial_number}-${amendmentNumber}` : form.value.sequencial_number.toString()}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="operation_number"
                                label="Código da Cessão"
                                value={form.value.operation_number}
                                onChange={(e) => handleChange("operation_number", e.target.value)}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="operation_dictionary_external_link"
                                label="Card JIRA"
                                value={form.value.external_link}
                                onChange={(e) => handleChange("external_link", e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "external_link")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="credit_product_type_name"
                                label="Tipo de Operação de Crédito"
                                value={form.value.credit_product_type_name}
                                onChange={(e) => handleChange("credit_product_type_name", e.target.value)}
                                items={CREDIT_OPERATION_TYPE_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "credit_product_type_name")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="receiving_account_name"
                                label="Conta para Recebimento"
                                value={form.value.receiving_account_name || ''}
                                onChange={(e) => handleChange('receiving_account_name', e.target.value)}
                                items={RECEIVING_ACCOUNT_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "receiving_account_name")}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="originador"
                                label="Originador"
                                value={form.value.originator}
                                onChange={(e) => handleChange("originator", e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="col-span-2 flex items-center pt-6">
                            <input
                                type="checkbox"
                                id="with_recourse"
                                checked={form.value.with_recourse}
                                onChange={(e) => handleChange("with_recourse", e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                Com Regresso
                            </label>
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-5">
                            <TextEdit
                                id="assignor"
                                label="Cedente"
                                value={form.value.assignor}
                                onChange={(e) => handleChange("assignor", e.target.value)}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-6 flex items-end gap-2">
                            <div className="flex-1">
                                <TextEdit
                                    id="recipient"
                                    label="Beneficiário"
                                    value={form.value.recipient}
                                    disabled={true}
                                />
                            </div>
                            <button type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 mb-4"
                                title="Buscar beneficiário"
                            >
                                <FaSearch />
                            </button>
                        </div>
                    </div>
                    <MainRiskSelector
                        value={form.value.main_risk}
                        onChange={(main_risk) => {
                            form.value = { ...form.value, main_risk };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isFormDisabled}
                        errorMessage={getErrorMessage(errorFields.value, "main_risk")}
                    />
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-2">
                            <TextEdit
                                label="Data de Criação"
                                value={form.value.created_at}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="emission_date"
                                label="Data de Emissão"
                                value={form.value.emission_date}
                                onChange={(e) => handleChange("emission_date", applyDateMask(e.target.value))}

                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="approved_on"
                                label="Data de Aprovação"
                                value={form.value.approved_on}
                                onChange={(e) => handleChange("approved_on", applyDateMask(e.target.value))}
                                placeholder="dd/mm/aaaa"
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_guarantee_percentage"
                                label="% aval da operação"
                                prefix="%"
                                fractionDigits={2}
                                value={form.value.guarantee_percentage}
                                onChange={(value) => handleChange("guarantee_percentage", value)}
                            />
                        </div>
                    </div>

                    <BusinessPartnerManager
                        value={businessPartner}
                        onChange={(partner) => {
                            setBusinessPartner(partner);
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoadingForm.value}
                    />

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Valor Financiado</h3>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="loan_amount"
                                label="Valor Financiado"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.loan_amount}
                                onChange={(value) => handleChange("loan_amount", value)}
                            />
                        </div>
                    </div>

                    <BaseCalculationSection
                        value={form.value.base_calculation}
                        onChange={(updated) => { form.value = { ...form.value, base_calculation: updated }; setHasUnsavedChanges(true); }}
                    />

                    <BillingConfigSection
                        value={form.value.billing_config || EMPTY_BILLING_CONFIG}
                        onChange={(updated) => { form.value = { ...form.value, billing_config: updated }; setHasUnsavedChanges(true); }}
                        disabled={isFormDisabled}
                        errorFields={errorFields.value}
                    />

                    <BillingStatusBadge billed={billingStatus.billed} billingInfo={billingStatus.billing_info} billingData={billingData} isPolling={isPolling} fetchBillingStatus={fetchBillingStatus} />

                    <DisbursementManager
                        disbursements={form.value.disbursement.disbursements}
                        totalAmount={form.value.disbursement.totalAmount}
                        onTotalAmountChange={(value) => {
                            const sum = form.value.disbursement.disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: value - sum,
                                disbursement: { ...form.value.disbursement, totalAmount: value }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onAdd={(disbursement) => {
                            const currentDisbursements = [...form.value.disbursement.disbursements, disbursement];
                            const sum = currentDisbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements: currentDisbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(index, disbursement) => {
                            const disbursements = form.value.disbursement.disbursements.map((d, i) => i === index ? disbursement : d);
                            const sum = disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                            const sum = disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoadingForm.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                label="Total dos Desembolsos"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.disbursement.totalAmount - (form.value.rebate_amount || 0)}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_rebate_amount"
                                label="Valor do Abatimento"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.rebate_amount}
                                onChange={(value) => handleChange("rebate_amount", value)}
                            />
                        </div>
                    </div>

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Vencimento</h3>
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_total_amount"
                                label="Valor Total"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.total_amount}
                                onChange={(value) => handleChange("total_amount", value)}
                            />
                        </div>
                        {form.value.with_recourse && (
                            <div className="col-span-2">
                                <TextEdit
                                    id="expiration_date"
                                    label="Data de Vencimento"
                                    value={form.value.expiration_date}
                                    onChange={(e) => handleChange("expiration_date", applyDateMask(e.target.value))}

                                />
                            </div>
                        )}
                    </div>

                    <ContractFileUpload
                        contractFiles={contractFiles}
                        existingFile={existingContractFile}
                        removedFile={removedContractFile}
                        onRemove={() => setRemovedContractFile(true)}
                        onRestore={() => setRemovedContractFile(false)}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                        onReceivablesExtracted={(items) => {
                            form.value = {
                                ...form.value,
                                receivables: {
                                    ...form.value.receivables,
                                    items,
                                    total: items.reduce((sum, item) => sum + item.receivable_amount, 0),
                                    quantity: items.length,
                                },
                            };
                            setHasUnsavedChanges(true);
                        }}
                    />

                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles([...removedArtifactFiles, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    {form.value.credit_product_type_name !== 'CONTRACT_ASSIGNMENT' && (
                        <InstallmentManager
                            installments={form.value.installments || []}
                            onAdd={(installment) => {
                                form.value = {
                                    ...form.value,
                                    installments: [...(form.value.installments || []), installment]
                                };
                                setHasUnsavedChanges(true);
                            }}
                            onUpdateItem={(index, installment) => {
                                const installments = (form.value.installments || []).map((inst, i) => i === index ? installment : inst);
                                form.value = {
                                    ...form.value,
                                    installments
                                };
                                setHasUnsavedChanges(true);
                            }}
                            onRemove={(index) => {
                                form.value = {
                                    ...form.value,
                                    installments: (form.value.installments || []).filter((_, i) => i !== index)
                                };
                                setHasUnsavedChanges(true);
                            }}
                            onUpdate={(updatedInstallments, lastDueDate) => {
                                form.value = {
                                    ...form.value,
                                    installments: updatedInstallments,
                                    expiration_date: lastDueDate || form.value.expiration_date
                                };
                                setHasUnsavedChanges(true);
                            }}
                            disabled={isFormDisabled}
                        />
                    )}

                    {/* Recebíveis - exibido quando NÃO é Cessão Contrato */}
                    {form.value.credit_product_type_name !== 'CONTRACT_ASSIGNMENT' && (
                        <>
                            <h3 className="text-md font-semibold mt-6 mb-3 text-gray-700 dark:text-gray-200">Recebíveis</h3>
                            <ReceivablesManager
                                receivables={form.value.receivables?.items || []}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                                onRowClick={(receivable) => {
                                    setSelectedReceivable(receivable);
                                    setIsReceivableModalOpen(true);
                                }}
                                onReceivablesUpdate={(updatedReceivables, spreadsheetProcessed, spreadsheetItemsCount) => {
                                    form.value = {
                                        ...form.value,
                                        receivables: {
                                            ...form.value.receivables,
                                            items: updatedReceivables,
                                            quantity: updatedReceivables.length,
                                            spreadsheet_processed: spreadsheetProcessed,
                                            spreadsheet_items_count: spreadsheetItemsCount
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                spreadsheetProcessed={form.value.receivables?.spreadsheet_processed || false}
                                onValidationChange={setHasInvalidDueDates}
                                overcollateral={form.value.receivables?.overcollateral}
                                onOvercollateralChange={(value) => {
                                    form.value = {
                                        ...form.value,
                                        receivables: {
                                            ...form.value.receivables,
                                            overcollateral: value
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                overcollateralError={getErrorMessage(errorFields.value, "overcollateral")}
                                reimportButton={
                                    <ReimportReceivablesButton
                                        sequencialNumber={form.value.sequencial_number}
                                        amendmentNumber={amendmentNumber}
                                        onReimported={(data) => handleJsonDataLoaded(data as unknown as JsonUploadData)}
                                        contractStatus={form.value.status}
                                    />
                                }
                            />
                        </>
                    )}

                    {/* Contrato cedido (contrato dado como recebível) - exibido quando É Cessão Contrato */}
                    {form.value.credit_product_type_name === 'CONTRACT_ASSIGNMENT' && (
                        <>
                            <h3 className="text-md font-semibold mt-6 mb-3 text-gray-700 dark:text-gray-200">Contrato cedido</h3>
                            <ContractsAsReceivablesManager
                                value={contractsAsReceivables}
                                onChange={(data) => {
                                    setContractsAsReceivables(data);
                                    form.value = {
                                        ...form.value,
                                        contracts_as_receivables: {
                                            items: data.items,
                                            total: data.items.reduce((s, i) => s + i.contract_value, 0),
                                            quantity: data.items.length
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                onFileDownload={(fileName) => handleDownload(credit_operation_id || '', fileName)}
                                disabled={isFormDisabled}
                                errorFields={errorFields.value}
                            />
                        </>
                    )}

                    {/* Garantias adicionais */}
                    <CollateralsTabManager
                        collaterals={form.value.collaterals}
                        onChange={(collaterals) => {
                            form.value = { ...form.value, collaterals };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isFormDisabled}
                    />

                </div>

                <PersonSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelect={handleSelectPerson}
                />

                {/* Modal de visualização de recebível */}
                <ReceivableModalView
                    isOpen={isReceivableModalOpen}
                    onClose={() => {
                        setIsReceivableModalOpen(false);
                        setSelectedReceivable(null);
                    }}
                    receivable={selectedReceivable}
                />

            </PageForm>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Ao finalizar, o contrato será finalizado e o registro será solicitado automaticamente (B3, Pismo e Dimensa). Deseja continuar?"
            />
            <AmendmentModal
                isOpen={isAmendmentModalOpen}
                onClose={() => setIsAmendmentModalOpen(false)}
                creditOperationId={credit_operation_id || ''}
                onSuccess={() => { navigate('/contracts/0/100/id/ASC/s=;'); }}
            />
        </Fragment>);
};

export default ReceivablesAssignmentPage;